// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';

/**
 * Reactively expose the SpeechSynthesis voice list. Some browsers
 * populate it asynchronously after `voiceschanged` fires; we listen
 * once and re-render. Returns an empty array if the API is missing
 * (Firefox without OS TTS, ancient browsers).
 */
export function useSpeechSynthesisVoices(): SpeechSynthesisVoice[] {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return [];
    return window.speechSynthesis.getVoices();
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const handler = () => setVoices(window.speechSynthesis.getVoices());
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    handler();
    return () => window.speechSynthesis.removeEventListener('voiceschanged', handler);
  }, []);

  return voices;
}
